/**
 * Gulcan Cart Page JavaScript
 * Handles AJAX cart operations: quantity changes, remove items, coupon codes
 * Inspired by Karaca.com.de
 */

(function() {
  'use strict';

  // ============================================
  // Configuration
  // ============================================
  const SELECTORS = {
    cart: '.gulcan-cart',
    cartItems: '[data-cart-items]',
    cartItem: '[data-cart-item]',
    cartSummary: '[data-cart-summary]',
    qtyInput: '[data-qty-input]',
    qtyDecrease: '[data-qty-decrease]',
    qtyIncrease: '[data-qty-increase]',
    removeItem: '[data-remove-item]',
    clearCart: '[data-clear-cart]',
    couponToggle: '.gulcan-cart__coupon-toggle',
    couponForm: '[data-coupon-form]',
    couponInput: '[data-coupon-input]',
    couponMessage: '[data-coupon-message]',
    shippingBar: '[data-shipping-bar]',
    cartSubtotal: '[data-cart-subtotal]',
    cartTotal: '[data-cart-total]',
    cartCount: '.gulcan-cart__count',
    linePrice: '[data-line-price]',
    progress: '[data-progress]'
  };

  // ============================================
  // State
  // ============================================
  let isUpdating = false;
  let cartContainer = null;
  let freeShippingThreshold = 5000; // Default €50 in cents

  // ============================================
  // Initialization
  // ============================================
  function init() {
    cartContainer = document.querySelector(SELECTORS.cart);
    if (!cartContainer) return;

    // Get free shipping threshold from data attribute
    const thresholdAttr = cartContainer.dataset.freeShippingThreshold;
    if (thresholdAttr) {
      freeShippingThreshold = parseInt(thresholdAttr, 10) * 100; // Convert € to cents
    }

    bindEvents();
  }

  // ============================================
  // Event Binding
  // ============================================
  function bindEvents() {
    // Quantity controls
    cartContainer.addEventListener('click', handleQuantityClick);
    cartContainer.addEventListener('change', handleQuantityChange);

    // Remove item
    cartContainer.addEventListener('click', handleRemoveClick);

    // Clear cart
    const clearBtn = cartContainer.querySelector(SELECTORS.clearCart);
    if (clearBtn) {
      clearBtn.addEventListener('click', handleClearCart);
    }

    // Coupon code toggle
    const couponToggle = cartContainer.querySelector(SELECTORS.couponToggle);
    if (couponToggle) {
      couponToggle.addEventListener('click', handleCouponToggle);
    }

    // Coupon code form submit
    const couponForm = cartContainer.querySelector(SELECTORS.couponForm);
    if (couponForm) {
      couponForm.addEventListener('submit', handleCouponSubmit);
    }
  }

  // ============================================
  // Quantity Handlers
  // ============================================
  function handleQuantityClick(e) {
    const decreaseBtn = e.target.closest(SELECTORS.qtyDecrease);
    const increaseBtn = e.target.closest(SELECTORS.qtyIncrease);

    if (!decreaseBtn && !increaseBtn) return;
    if (isUpdating) return;

    const cartItem = e.target.closest(SELECTORS.cartItem);
    if (!cartItem) return;

    const input = cartItem.querySelector(SELECTORS.qtyInput);
    if (!input) return;

    let quantity = parseInt(input.value, 10);

    if (decreaseBtn) {
      quantity = Math.max(1, quantity - 1);
    } else if (increaseBtn) {
      quantity = Math.min(99, quantity + 1);
    }

    input.value = quantity;
    updateCartItem(cartItem, quantity);
  }

  function handleQuantityChange(e) {
    if (!e.target.matches(SELECTORS.qtyInput)) return;
    if (isUpdating) return;

    const cartItem = e.target.closest(SELECTORS.cartItem);
    if (!cartItem) return;

    let quantity = parseInt(e.target.value, 10);

    // Validate quantity
    if (isNaN(quantity) || quantity < 1) {
      quantity = 1;
    } else if (quantity > 99) {
      quantity = 99;
    }

    e.target.value = quantity;
    updateCartItem(cartItem, quantity);
  }

  // ============================================
  // Remove Item Handler
  // ============================================
  function handleRemoveClick(e) {
    const removeBtn = e.target.closest(SELECTORS.removeItem);
    if (!removeBtn) return;
    if (isUpdating) return;

    const cartItem = e.target.closest(SELECTORS.cartItem);
    if (!cartItem) return;

    // Add removing animation
    cartItem.classList.add('is-removing');

    // Wait for animation, then update
    setTimeout(() => {
      updateCartItem(cartItem, 0);
    }, 300);
  }

  // ============================================
  // Clear Cart Handler
  // ============================================
  function handleClearCart(e) {
    e.preventDefault();
    if (isUpdating) return;

    if (!confirm('Weet je zeker dat je je winkelwagen wilt legen?')) {
      return;
    }

    setLoading(true);

    fetch('/cart/clear.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      }
    })
    .then(response => response.json())
    .then(() => {
      // Reload page to show empty cart state
      window.location.reload();
    })
    .catch(error => {
      console.error('Error clearing cart:', error);
      setLoading(false);
    });
  }

  // ============================================
  // Coupon Code Handlers
  // ============================================
  function handleCouponToggle(e) {
    const toggle = e.currentTarget;
    const formWrapper = document.querySelector('#coupon-form');

    if (!formWrapper) return;

    const isExpanded = toggle.getAttribute('aria-expanded') === 'true';

    toggle.setAttribute('aria-expanded', !isExpanded);
    formWrapper.hidden = isExpanded;

    // Focus input when opening
    if (!isExpanded) {
      const input = formWrapper.querySelector(SELECTORS.couponInput);
      if (input) {
        setTimeout(() => input.focus(), 100);
      }
    }
  }

  function handleCouponSubmit(e) {
    e.preventDefault();

    const input = cartContainer.querySelector(SELECTORS.couponInput);
    const message = cartContainer.querySelector(SELECTORS.couponMessage);
    const code = input ? input.value.trim() : '';

    if (!code) {
      showCouponMessage('Voer een kortingscode in', 'error');
      return;
    }

    // Store code and redirect to checkout with discount parameter
    // Shopify checkout accepts discount codes via URL parameter
    const checkoutUrl = '/checkout?discount=' + encodeURIComponent(code);

    showCouponMessage('Code wordt toegepast...', 'success');

    // Small delay to show message, then redirect
    setTimeout(() => {
      window.location.href = checkoutUrl;
    }, 500);
  }

  function showCouponMessage(text, type) {
    const message = cartContainer.querySelector(SELECTORS.couponMessage);
    if (!message) return;

    message.textContent = text;
    message.className = 'gulcan-cart__coupon-message ' + type;
    message.hidden = false;

    // Auto-hide error messages after 3 seconds
    if (type === 'error') {
      setTimeout(() => {
        message.hidden = true;
      }, 3000);
    }
  }

  // ============================================
  // Cart Update Functions
  // ============================================
  function updateCartItem(cartItem, quantity) {
    const lineKey = cartItem.dataset.lineKey;
    if (!lineKey) return;

    setLoading(true);
    cartItem.classList.add('is-updating');

    fetch('/cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        id: lineKey,
        quantity: quantity
      })
    })
    .then(response => {
      // Check for Shopify error responses (e.g., inventory issues)
      if (!response.ok) {
        return response.json().then(err => Promise.reject(err));
      }
      return response.json();
    })
    .then(cart => {
      // Check if Shopify returned an error in the response body
      if (cart.status && cart.status >= 400) {
        throw new Error(cart.description || 'Er ging iets mis');
      }

      if (quantity === 0 || cart.item_count === 0) {
        // Item removed or cart empty - reload page
        window.location.reload();
      } else {
        // Update the page with new cart data
        updateCartUI(cart);
        cartItem.classList.remove('is-updating');
      }
    })
    .catch(error => {
      console.error('Error updating cart:', error);
      cartItem.classList.remove('is-updating');
      setLoading(false);

      // Show user-friendly error message
      const errorMessage = error.description || error.message || 'Er ging iets mis bij het bijwerken van je winkelwagen';
      showCartError(errorMessage);

      // Reload page after short delay to sync cart state
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    });
  }

  function showCartError(message) {
    // Create temporary error toast
    const toast = document.createElement('div');
    toast.className = 'gulcan-cart__toast gulcan-cart__toast--error';
    toast.textContent = message;
    toast.setAttribute('role', 'alert');

    document.body.appendChild(toast);

    // Trigger animation
    requestAnimationFrame(() => {
      toast.classList.add('is-visible');
    });

    // Remove after 3 seconds
    setTimeout(() => {
      toast.classList.remove('is-visible');
      setTimeout(() => toast.remove(), 300);
    }, 3000);
  }

  function updateCartUI(cart) {
    // Update cart count
    const countEl = cartContainer.querySelector(SELECTORS.cartCount);
    if (countEl) {
      const itemText = cart.item_count === 1 ? 'item' : 'items';
      countEl.textContent = `(${cart.item_count} ${itemText})`;
    }

    // Update subtotal
    const subtotalEl = cartContainer.querySelector(SELECTORS.cartSubtotal);
    if (subtotalEl) {
      subtotalEl.textContent = formatMoney(cart.total_price);
    }

    // Update total
    const totalEl = cartContainer.querySelector(SELECTORS.cartTotal);
    if (totalEl) {
      totalEl.textContent = formatMoney(cart.total_price);
    }

    // Update line prices
    cart.items.forEach(item => {
      const itemEl = cartContainer.querySelector(`[data-line-key="${item.key}"]`);
      if (itemEl) {
        const priceEl = itemEl.querySelector(SELECTORS.linePrice);
        if (priceEl) {
          priceEl.textContent = formatMoney(item.final_line_price);
        }

        // Update quantity input
        const qtyInput = itemEl.querySelector(SELECTORS.qtyInput);
        if (qtyInput) {
          qtyInput.value = item.quantity;
        }

        // Update decrease button state
        const decreaseBtn = itemEl.querySelector(SELECTORS.qtyDecrease);
        if (decreaseBtn) {
          decreaseBtn.disabled = item.quantity <= 1;
        }
      }
    });

    // Update free shipping bar
    updateShippingBar(cart);

    // Update header cart count (if exists)
    updateHeaderCartCount(cart.item_count);

    // Dispatch custom event for other scripts
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));

    setLoading(false);
  }

  function updateShippingBar(cart) {
    const shippingBar = cartContainer.querySelector(SELECTORS.shippingBar);
    if (!shippingBar) return;

    // Check detected country (set by gulcan-pdp.js)
    const countryCode = window.gulcanDetectedCountry || 'NL';
    const isNL = countryCode === 'NL';

    // Show/hide appropriate shipping sections
    const nlSection = shippingBar.querySelector('[data-shipping-nl]');
    const intlSection = shippingBar.querySelector('[data-shipping-international]');

    if (nlSection && intlSection) {
      if (isNL) {
        nlSection.style.display = 'block';
        intlSection.style.display = 'none';
      } else {
        nlSection.style.display = 'none';
        intlSection.style.display = 'block';

        // Update international shipping text
        const shippingCost = window.gulcanShippingCost || 6.95;
        const countryNames = {
          BE: 'België', DE: 'Duitsland', FR: 'Frankrijk', AT: 'Oostenrijk',
          LU: 'Luxemburg', IT: 'Italië', ES: 'Spanje', GB: 'Verenigd Koninkrijk'
        };
        const countryName = countryNames[countryCode] || countryCode;
        const priceFormatted = shippingCost.toFixed(2).replace('.', ',');

        const costText = intlSection.querySelector('[data-shipping-cost-text]');
        const countryText = intlSection.querySelector('[data-shipping-country-text]');
        if (costText) costText.textContent = `Verzendkosten: vanaf €${priceFormatted}`;
        if (countryText) countryText.textContent = `in ${countryName}`;
      }
    }

    // Only show progress bar for NL
    if (!isNL) return;

    const remaining = freeShippingThreshold - cart.total_price;
    const progressPercent = Math.min(100, (cart.total_price / freeShippingThreshold) * 100);

    // Update progress bar
    const progressEl = shippingBar.querySelector(SELECTORS.progress);
    if (progressEl) {
      progressEl.style.width = `${progressPercent}%`;
    }

    // Update text
    const textEl = shippingBar.querySelector('.gulcan-cart__shipping-text');
    const successEl = shippingBar.querySelector('.gulcan-cart__shipping-success');

    if (remaining <= 0) {
      // Free shipping achieved
      if (textEl) textEl.style.display = 'none';
      if (successEl) successEl.style.display = 'flex';
    } else {
      // Still need more
      if (textEl) {
        textEl.style.display = 'block';
        textEl.innerHTML = `Nog <strong>${formatMoney(remaining)}</strong> voor gratis verzending`;
      }
      if (successEl) successEl.style.display = 'none';
    }

    // Update shipping row in prices
    const freeEl = cartContainer.querySelector('.gulcan-cart__free');
    const noteEl = cartContainer.querySelector('.gulcan-cart__shipping-note');

    if (remaining <= 0) {
      if (freeEl) freeEl.style.display = 'inline';
      if (noteEl) noteEl.style.display = 'none';
    } else {
      if (freeEl) freeEl.style.display = 'none';
      if (noteEl) noteEl.style.display = 'inline';
    }
  }

  function updateHeaderCartCount(count) {
    // Update various header cart count elements
    const selectors = [
      '.gulcan-header__cart-count',
      '#gulcan-cart-count',
      '.cart-link__count',
      '.cart-count',
      '[data-cart-count]'
    ];

    selectors.forEach(selector => {
      document.querySelectorAll(selector).forEach(el => {
        el.textContent = count;
      });
    });
  }

  // ============================================
  // Utility Functions
  // ============================================
  function setLoading(loading) {
    isUpdating = loading;
    if (cartContainer) {
      cartContainer.classList.toggle('is-loading', loading);
    }
  }

  function formatMoney(cents) {
    // Format cents to Euro currency (Dutch format)
    const euros = (cents / 100).toFixed(2);
    return '€' + euros.replace('.', ',');
  }

  // ============================================
  // Initialize on DOM Ready
  // ============================================
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
